/**
 * <instagram-shoppable>
 *
 * Renders a shoppable Instagram carousel. Media comes from the proxy defined on
 * data-endpoint; product titles and prices are read live from Shopify via
 * /products/{handle}.js so they can never drift out of date.
 *
 * Product handles are written in the Instagram caption as:
 *   #shop:linen-skirt,black-tank
 *   #shop:linen-skirt@32x58        (optional hotspot at 32% x / 58% y)
 * They can also be overridden per post from the section settings.
 */
(function () {
  'use strict';

  var productCache = {};

  function money(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function rootUrl() {
    return (window.theme && theme.routes && theme.routes.home) || '/';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** Fetch a product once, then serve it from memory + sessionStorage. */
  function getProduct(handle) {
    if (productCache[handle]) return productCache[handle];

    var cacheKey = 'ig-shop:product:' + handle;
    try {
      var stored = sessionStorage.getItem(cacheKey);
      if (stored) {
        var parsed = JSON.parse(stored);
        if (Date.now() - parsed.at < 15 * 60 * 1000) {
          productCache[handle] = Promise.resolve(parsed.data);
          return productCache[handle];
        }
      }
    } catch (e) {
      /* private mode, ignore */
    }

    var base = rootUrl().replace(/\/$/, '');
    productCache[handle] = fetch(base + '/products/' + handle + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Product not found: ' + handle);
        return res.json();
      })
      .then(function (data) {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data: data }));
        } catch (e) {
          /* quota, ignore */
        }
        return data;
      })
      .catch(function () {
        // Cache the miss so a deleted handle does not re-request on every card.
        return null;
      });

    return productCache[handle];
  }

  var ICONS = {
    tag:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21.41 11.58 12.41 2.58A2 2 0 0 0 11 2H4a2 2 0 0 0-2 2v7a2 2 0 0 0 .59 1.42l9 9a2 2 0 0 0 2.82 0l7-7a2 2 0 0 0 0-2.84ZM6.5 8A1.5 1.5 0 1 1 8 6.5 1.5 1.5 0 0 1 6.5 8Z"/></svg>',
    carousel:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h11a3 3 0 0 1 3 3v11h-2V6a1 1 0 0 0-1-1H7Zm-4 4h11a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z"/></svg>',
    video: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7L8 5Z"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>'
  };

  class InstagramShoppable extends HTMLElement {
    connectedCallback() {
      this.endpoint = this.dataset.endpoint;
      this.limit = parseInt(this.dataset.limit, 10) || 12;
      this.shoppableOnly = this.dataset.shoppableOnly === 'true';
      this.showPrice = this.dataset.showPrice !== 'false';
      this.autoplay = this.dataset.autoplayVideo !== 'false';
      this.hasBlocks = this.dataset.hasBlocks === 'true';
      this.overrides = this.parseOverrides(this.dataset.overrides);

      this.track = this.querySelector('[data-ig-track]');
      this.prevBtn = this.querySelector('[data-ig-prev]');
      this.nextBtn = this.querySelector('[data-ig-next]');
      this.progress = this.querySelector('[data-ig-progress]');
      this.progressBar = this.querySelector('[data-ig-progress-bar]');

      this.reducedMotion = Boolean(
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
      this.autoScroll = this.dataset.autoScroll === 'true' && !this.reducedMotion;
      this.autoSeconds = parseFloat(this.dataset.autoScrollSeconds) || 4;
      this.wantHint = this.dataset.swipeHint !== 'false' && !this.reducedMotion;
      this.tapToShop = this.dataset.tapToShop !== 'false';

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
      if (this.track) {
        this.track.addEventListener(
          'scroll',
          () => {
            this.updateArrows();
            this.updateProgress();
          },
          { passive: true }
        );
        // Any deliberate touch, wheel or key hands control back to the customer.
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((evt) =>
          this.track.addEventListener(evt, () => this.stopAuto(), { passive: true })
        );
      }

      this.addEventListener('click', (event) => this.onClick(event));

      // Server-rendered mode: the tiles are already in the HTML. Wire up the
      // carousel and video autoplay, and make no network requests at all.
      if (this.hasBlocks) {
        this.updateArrows();
        this.updateProgress();
        this.setupMotion();
        if (this.autoplay) this.observeVideos();
        return;
      }

      if (!this.endpoint) {
        this.renderError('Add a Post block, or set a Feed URL, in the section settings.');
        return;
      }

      // A common mistake: pasting the Instagram profile URL instead of the proxy.
      // It returns HTML and is blocked by CORS, so catch it before the request.
      if (/(^|\/\/)(www\.)?instagram\.com/i.test(this.endpoint)) {
        this.renderError(
          'That is your Instagram profile URL. Feed URL needs your deployed proxy, ending in /api/instagram.'
        );
        return;
      }

      // Only hit the network when the section is close to the viewport.
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              observer.disconnect();
              this.load();
            }
          },
          { rootMargin: '400px' }
        );
        observer.observe(this);
      } else {
        this.load();
      }
    }

    parseOverrides(raw) {
      if (!raw) return {};
      try {
        return JSON.parse(raw) || {};
      } catch (e) {
        console.warn('[instagram-shoppable] Product override JSON is invalid, ignoring it.');
        return {};
      }
    }

    load() {
      var url = new URL(this.endpoint, window.location.origin);
      url.searchParams.set('limit', this.limit);
      if (this.shoppableOnly) url.searchParams.set('shoppable', '1');

      fetch(url.toString(), { headers: { Accept: 'application/json' } })
        .then((res) => {
          if (!res.ok) throw new Error('Feed request failed (' + res.status + ')');
          return res.json();
        })
        .then((data) => {
          var posts = (data && data.posts) || [];
          if (!posts.length) {
            this.renderError('No Instagram posts to show yet.');
            return;
          }
          this.render(posts);
        })
        .catch((error) => {
          console.error('[instagram-shoppable]', error);
          this.renderError('');
        });
    }

    renderError(message) {
      if (!this.track) return;
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;

      var inEditor = Boolean(window.Shopify && window.Shopify.designMode);

      // On the live storefront an unreachable feed should leave no trace, rather
      // than a tall empty gap. In the theme editor, say what is wrong.
      if (!inEditor) {
        var section = this.closest('.shopify-section') || this;
        section.style.display = 'none';
        return;
      }

      this.track.innerHTML =
        '<p class="ig-shop__error">' +
        escapeHtml(message || 'Could not reach the Instagram feed. Check the Feed URL in this section.') +
        '</p>';
      this.track.style.display = 'block';
    }

    /** Caption-parsed products, unless the section settings override this post. */
    productsFor(post) {
      var override = this.overrides[post.shortcode] || this.overrides[post.id];
      if (!override) return post.products || [];

      var list = Array.isArray(override) ? override : override.products || [];
      return list.map(function (entry) {
        if (typeof entry === 'string') return { handle: entry, x: null, y: null };
        return { handle: entry.handle, x: entry.x == null ? null : entry.x, y: entry.y == null ? null : entry.y };
      });
    }

    render(posts) {
      var html = posts.map((post) => this.cardHtml(post)).join('');
      this.track.innerHTML = html;
      this.updateArrows();

      posts.forEach((post, index) => {
        var products = this.productsFor(post);
        if (products.length) this.hydrateProducts(this.track.children[index], products);
      });

      if (this.autoplay) this.observeVideos();
    }

    cardHtml(post) {
      var products = this.productsFor(post);
      var isVideo = post.is_video;
      var isCarousel = post.media_type === 'CAROUSEL_ALBUM';
      var alt = escapeHtml((post.caption || 'Instagram post').slice(0, 120));

      var media = isVideo
        ? '<video src="' +
          escapeHtml(post.src) +
          '" poster="' +
          escapeHtml(post.poster || '') +
          '" muted loop playsinline preload="none" aria-label="' +
          alt +
          '"></video>'
        : '<img src="' +
          escapeHtml(post.src) +
          '" alt="' +
          alt +
          '" loading="lazy" decoding="async">';

      var badge = '';
      if (isVideo) badge = '<span class="ig-shop__media-badge">' + ICONS.video + '</span>';
      else if (isCarousel) badge = '<span class="ig-shop__media-badge">' + ICONS.carousel + '</span>';

      var tagMarkup = '';
      if (products.length) {
        var positioned = products.filter(function (p) {
          return p.x !== null && p.y !== null;
        });

        if (positioned.length) {
          tagMarkup = positioned
            .map(function (p) {
              return (
                '<button type="button" class="ig-shop__hotspot" data-ig-toggle aria-label="Shop this look" style="left:' +
                p.x +
                '%;top:' +
                p.y +
                '%"></button>'
              );
            })
            .join('');
        } else {
          tagMarkup =
            '<button type="button" class="ig-shop__tag-toggle" data-ig-toggle aria-label="Shop this look">' +
            ICONS.tag +
            '</button>';
        }

        tagMarkup +=
          '<div class="ig-shop__products" data-ig-products>' +
          '<button type="button" class="ig-shop__products-close" data-ig-close aria-label="Close">&times;</button>' +
          products
            .map(function () {
              return '<div class="ig-shop__product-row"><span class="ig-shop__product-image ig-shop__skeleton"></span><span class="ig-shop__product-info"><span class="ig-shop__product-title ig-shop__skeleton">&nbsp;</span></span></div>';
            })
            .join('') +
          '</div>';
      }

      return (
        '<div class="ig-shop__item" data-ig-item>' +
        '<a class="ig-shop__link" href="' +
        escapeHtml(post.permalink) +
        '" target="_blank" rel="noopener" aria-label="View on Instagram"></a>' +
        media +
        badge +
        tagMarkup +
        '</div>'
      );
    }

    /** Replace the skeleton rows once Shopify returns real product data. */
    hydrateProducts(card, products) {
      var container = card.querySelector('[data-ig-products]');
      if (!container) return;
      var showPrice = this.showPrice;
      var base = rootUrl().replace(/\/$/, '');

      Promise.all(products.map((p) => getProduct(p.handle))).then(function (results) {
        var rows = results
          .map(function (product) {
            if (!product) return '';

            var variant = null;
            for (var i = 0; i < product.variants.length; i++) {
              if (product.variants[i].available) {
                variant = product.variants[i];
                break;
              }
            }
            var available = Boolean(variant);
            if (!variant) variant = product.variants[0];

            var priceHtml = '';
            if (showPrice) {
              if (!available) {
                priceHtml = '<span class="ig-shop__sold-out">Sold out</span>';
              } else {
                priceHtml = money(variant.price);
                if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                  priceHtml += '<s>' + money(variant.compare_at_price) + '</s>';
                }
              }
            }

            var image = product.featured_image
              ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2')
              : '';

            return (
              '<div class="ig-shop__product-row">' +
              '<a class="ig-shop__product" href="' +
              base +
              '/products/' +
              escapeHtml(product.handle) +
              '">' +
              (image
                ? '<img class="ig-shop__product-image" src="' +
                  escapeHtml(image) +
                  '" alt="" loading="lazy" width="48" height="60">'
                : '<span class="ig-shop__product-image"></span>') +
              '<span class="ig-shop__product-info">' +
              '<span class="ig-shop__product-title">' +
              escapeHtml(product.title) +
              '</span>' +
              (priceHtml ? '<span class="ig-shop__product-price">' + priceHtml + '</span>' : '') +
              '</span>' +
              '</a>' +
              '<button type="button" class="ig-shop__product-cta" data-ig-add="' +
              variant.id +
              '"' +
              (available ? '' : ' disabled') +
              '>' +
              (available ? 'Add' : 'Sold out') +
              '</button>' +
              '</div>'
            );
          })
          .filter(Boolean)
          .join('');

        if (!rows) {
          // Every handle 404'd - drop the tag affordance entirely.
          container.remove();
          var toggle = card.querySelector('[data-ig-toggle]');
          if (toggle) toggle.remove();
          return;
        }

        container.innerHTML =
          '<button type="button" class="ig-shop__products-close" data-ig-close aria-label="Close">&times;</button>' +
          rows;
      });
    }

    onClick(event) {
      var toggle = event.target.closest('[data-ig-toggle]');
      if (toggle) {
        event.preventDefault();
        var card = toggle.closest('[data-ig-item]');
        this.closeAll(card);
        card.classList.toggle('is-open');
        return;
      }

      // Tapping the clip itself opens its products, like Instagram. Clicks
      // inside the open panel are left alone so the links and Add buttons
      // below still work.
      if (this.tapToShop && !event.target.closest('[data-ig-products]')) {
        var tile = event.target.closest('[data-ig-item]');
        if (tile && tile.querySelector('[data-ig-products]')) {
          event.preventDefault();
          this.stopAuto();
          var wasOpen = tile.classList.contains('is-open');
          this.closeAll(tile);
          tile.classList.toggle('is-open', !wasOpen);
          return;
        }
      }

      if (event.target.closest('[data-ig-close]')) {
        event.preventDefault();
        var openCard = event.target.closest('[data-ig-item]');
        if (openCard) openCard.classList.remove('is-open');
        return;
      }

      var addBtn = event.target.closest('[data-ig-add]');
      if (addBtn) {
        event.preventDefault();
        this.addToCart(addBtn);
      }
    }

    closeAll(except) {
      this.querySelectorAll('[data-ig-item].is-open').forEach(function (card) {
        if (card !== except) card.classList.remove('is-open');
      });
    }

    addToCart(button) {
      var id = button.dataset.igAdd;
      var original = button.textContent;
      button.classList.add('is-loading');
      button.textContent = '...';

      var url = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(function () {
          button.textContent = 'Added';
          // Let the theme's cart drawer pick the change up.
          document.dispatchEvent(new CustomEvent('cart:build'));
          document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
          setTimeout(function () {
            button.textContent = original;
            button.classList.remove('is-loading');
          }, 1600);
        })
        .catch(function () {
          button.textContent = 'Error';
          setTimeout(function () {
            button.textContent = original;
            button.classList.remove('is-loading');
          }, 1600);
        });
    }

    observeVideos() {
      var videos = this.querySelectorAll('video');
      if (!videos.length || !('IntersectionObserver' in window)) return;

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              var play = video.play();
              if (play && play.catch) play.catch(function () {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.4 }
      );

      videos.forEach(function (video) {
        observer.observe(video);
      });
    }

    scrollByPage(direction) {
      if (!this.track) return;
      var first = this.track.firstElementChild;
      if (!first) return;
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (first.offsetWidth + gap), behavior: 'smooth' });
    }

    updateArrows() {
      if (!this.track || !this.prevBtn || !this.nextBtn) return;
      var max = this.track.scrollWidth - this.track.clientWidth;
      this.prevBtn.disabled = this.track.scrollLeft <= 2;
      this.nextBtn.disabled = this.track.scrollLeft >= max - 2;
    }

    /** How much of the row is scrollable, and where we are in it. */
    isScrollable() {
      return Boolean(this.track) && this.track.scrollWidth - this.track.clientWidth > 8;
    }

    updateProgress() {
      if (!this.progress || !this.progressBar) return;

      if (!this.isScrollable()) {
        this.progress.hidden = true;
        return;
      }
      this.progress.hidden = false;

      var max = this.track.scrollWidth - this.track.clientWidth;
      var ratio = max > 0 ? this.track.scrollLeft / max : 0;

      // Thumb width mirrors how much of the row is on screen; it then travels
      // the leftover space. Working in pixels keeps this readable.
      var railPx = this.progress.clientWidth;
      var thumbPx = Math.max(railPx * (this.track.clientWidth / this.track.scrollWidth), 24);
      this.progressBar.style.width = thumbPx + 'px';
      this.progressBar.style.transform = 'translateX(' + ratio * (railPx - thumbPx) + 'px)';
    }

    /**
     * Mobile hides the arrows, so without a cue nothing says the row scrolls.
     * A single short nudge when the section first comes into view reads as
     * "this moves" without hijacking the page. Runs once per page view, and
     * never when the visitor prefers reduced motion.
     */
    setupMotion() {
      if (!this.track) return;
      if (!this.wantHint && !this.autoScroll) return;
      if (!('IntersectionObserver' in window)) return;

      var observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              this.pauseAuto();
              return;
            }
            if (this.wantHint && !this.hinted) {
              this.hinted = true;
              this.nudge();
            }
            if (this.autoScroll) this.startAuto();
          });
        },
        { threshold: 0.35 }
      );
      observer.observe(this);
      this.motionObserver = observer;

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAuto();
        else if (this.autoScroll && !this.autoStopped) this.startAuto();
      });
    }

    nudge() {
      if (!this.isScrollable() || this.track.scrollLeft > 4) return;
      var peek = Math.min(46, this.track.clientWidth * 0.12);
      this.track.scrollTo({ left: peek, behavior: 'smooth' });
      setTimeout(() => {
        if (!this.autoStopped) this.track.scrollTo({ left: 0, behavior: 'smooth' });
      }, 620);
    }

    startAuto() {
      if (!this.autoScroll || this.autoStopped || this.autoTimer) return;
      if (!this.isScrollable()) return;
      this.autoTimer = setInterval(() => {
        if (!this.track) return;
        var max = this.track.scrollWidth - this.track.clientWidth;
        if (this.track.scrollLeft >= max - 4) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollByPage(1);
        }
      }, Math.max(2, this.autoSeconds) * 1000);
    }

    pauseAuto() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    }

    /** Once the customer takes over, stop advancing for good. */
    stopAuto() {
      this.autoStopped = true;
      this.pauseAuto();
    }

    disconnectedCallback() {
      this.pauseAuto();
      if (this.motionObserver) this.motionObserver.disconnect();
    }
  }

  if (!customElements.get('instagram-shoppable')) {
    customElements.define('instagram-shoppable', InstagramShoppable);
  }

  // Expose the arrow icons so the Liquid template stays markup-only.
  window.InstagramShoppableIcons = ICONS;
})();
